import React, { useMemo, useState } from "react";
import * as Mui from "@mui/material";
import { withStyles } from "tss-react/mui";
import type { WithStyles } from "type/tss";
import MuiAlert from "@mui/material/Alert";
import * as Icons from "react-feather";
import * as Router from "react-router-dom";
import { DashboardLayout } from "component/layout/dashboard";
import { FullPageCard } from "component/shared/full-page-card";
import { FeatherIcon } from "component/shared/feather-icon";
import { ColdFusionIframe } from "component/shared/iframe/coldfusion";
import { Select } from "component/shared/select";
import * as api from "api/api";
import { PageProps } from "shared/page-props";
import { coldFusionReplatform } from "shared/env";
import { styles } from "./style";

interface Props extends PageProps, Router.RouteComponentProps, WithStyles<typeof styles> {}

const TITLE = "Export Leads";

interface SortOption {
	value: string;
	label: string;
}

// CF OrderBy options (subscriber_export_output_1.cfm:36-43); "agent" only for OfficeAccountType accounts.
const BASE_SORT_OPTIONS: SortOption[] = [
	{ value: "name", label: "Name" },
	{ value: "email", label: "Email" },
	{ value: "datestamp", label: "Date" },
];

const ColdFusionFallback: React.FC<Props> = ({ user }) => (
	<DashboardLayout
		permitted={user && user.permissions.leads}
		title={TITLE}
		header={
			<Mui.Typography variant="h1">
				<FeatherIcon>
					<Icons.User />
				</FeatherIcon>
				{TITLE}
			</Mui.Typography>
		}
		iframe={<ColdFusionIframe url="/subscriber_export.cfm" />}
	/>
);

const LeadExportBase: React.FC<Props> = ({ user, classes = {} }) => {
	const [orderBy, setOrderBy] = useState("name");
	const [includeDisabled, setIncludeDisabled] = useState(false);
	const [includeDeleted, setIncludeDeleted] = useState(false);
	const [exporting, setExporting] = useState(false);
	const [snackbar, setSnackbar] = useState<{ open: boolean; message: string }>({ open: false, message: "" });

	// CF gates the "Agent" option on OfficeAccountTypeYN (subscriber_export_output_1.cfm:40-42),
	// not the AGENT_OFFICE-derived broker flag.
	const sortOptions = useMemo(
		() => (user.permissions.officeAccountType ? [...BASE_SORT_OPTIONS, { value: "agent", label: "Agent" }] : BASE_SORT_OPTIONS),
		[user.permissions.officeAccountType]
	);

	const handleExport = async () => {
		setExporting(true);
		try {
			await api.exportLeads(orderBy, includeDisabled, includeDeleted);
		} catch{
			setSnackbar({ open: true, message: "Failed to export leads" });
		} finally {
			setExporting(false);
		}
	};

	return (
		<DashboardLayout
			permitted={user && user.permissions.leads}
			title={TITLE}
			header={
				<Mui.Typography variant="h1">
					<FeatherIcon>
						<Icons.User />
					</FeatherIcon>
					{TITLE}
				</Mui.Typography>
			}
		>
			<FullPageCard>
				{/* CF body heading is "Lead Export" (page/tab title stays "Export Leads"). */}
				<Mui.Typography variant="h6" className={classes.heading}>
					Lead Export
				</Mui.Typography>

				<Mui.Grid container direction="column" spacing={3} className={classes.form}>
					<Mui.Grid item>
						<Select
							variant="outlined"
							label="Sort order"
							value={orderBy}
							options={sortOptions}
							valueExtractor={(option: SortOption) => option.value}
							labelExtractor={(option: SortOption) => option.label}
							optionRenderer={(option: SortOption) => <Mui.ListItemText primary={option.label} />}
							onChange={(value) => setOrderBy(value == null || value === "" ? "name" : String(value))}
						/>
					</Mui.Grid>
					<Mui.Grid item>
						<Mui.FormControlLabel
							control={
								<Mui.Checkbox
									color="primary"
									checked={includeDisabled}
									onChange={(e) => setIncludeDisabled(e.target.checked)}
									data-testid="export-include-disabled"
								/>
							}
							label="Include Disabled Leads"
						/>
						<Mui.FormControlLabel
							control={
								<Mui.Checkbox
									color="primary"
									checked={includeDeleted}
									onChange={(e) => setIncludeDeleted(e.target.checked)}
									data-testid="export-include-deleted"
								/>
							}
							label="Include Deleted Leads"
						/>
					</Mui.Grid>
					<Mui.Grid item>
						<Mui.Button
							variant="contained"
							color="primary"
							onClick={handleExport}
							disabled={exporting}
							data-testid="export-submit"
						>
							{exporting ? "Exporting..." : "Export"}
						</Mui.Button>
					</Mui.Grid>
				</Mui.Grid>
			</FullPageCard>

			<Mui.Snackbar
				open={snackbar.open}
				autoHideDuration={4000}
				onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
			>
				<MuiAlert severity="error">
					<Mui.Typography>{snackbar.message}</Mui.Typography>
				</MuiAlert>
			</Mui.Snackbar>
		</DashboardLayout>
	);
};

const LeadExport = withStyles(LeadExportBase, styles);

const LeadExportPageBase: React.FC<Props> = (props) => {
	if (!coldFusionReplatform) {
		return <ColdFusionFallback {...props} />;
	}
	return <LeadExport {...props} />;
};

export const LeadExportPage = withStyles(LeadExportPageBase, styles);
